import { Switch, Route, Router } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/landing";
import Dashboard from "@/pages/dashboard";
import PlayerDatabase from "@/pages/player-database";
import Shortlists from "@/pages/shortlists";
import EmailManagement from "@/pages/email-management";
import EmailSettings from "@/pages/email-settings";
import Feedback from "@/pages/feedback";
import Profile from "@/pages/profile";
import Updates from "@/pages/updates";
import TeamManagement from "@/pages/team-management";
import TeamInvitation from "@/pages/team-invitation";
import Login from "@/pages/auth/login";
import Register from "@/pages/auth/register";
import SocialSignup from "@/pages/auth/social-signup";
import { AuthProvider } from "@/hooks/use-auth";
import { OAuthProvider } from "@/contexts/OAuthContext";
import { ProtectedRoute } from "@/lib/auth";
import AppShell from "@/components/layout/AppShell";
import { supabase } from '@/lib/supa';
import { useEffect, useState } from 'react';
import PrivacyPolicy from "@/pages/privacy-policy";
import TermsOfService from "@/pages/terms-of-service";
import CookiePolicy from "@/pages/cookie-policy";
import FERPACompliance from "@/pages/ferpa-compliance";
import Pricing from "@/pages/pricing";

import { SentryErrorBoundary } from "@/components/ErrorBoundary";


// OAuth redirect loading component
function OAuthRedirectLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  
  return (
    <Switch>
      {/* Public routes */}
      <Route path="/" component={Landing} />
      <Route path="/login" component={Login} />
      <Route path="/auth/login" component={Login} /> {/* OAuth redirect compatibility */}
      <Route path="/register" component={Register} />
      <Route path="/social-signup" component={SocialSignup} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/terms-of-service" component={TermsOfService} />
      <Route path="/cookie-policy" component={CookiePolicy} />
      <Route path="/ferpa-compliance" component={FERPACompliance} />
      <Route path="/pricing" component={Pricing} />
      
      {/* Team invitation route (public with token) */}
      <Route path="/teams/join" component={TeamInvitation} />

      
      {/* Protected routes with AppShell */}
      <Route path="/dashboard">
        <ProtectedRoute>
          <AppShell>
            <Dashboard />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/players">
        <ProtectedRoute>
          <AppShell>
            <PlayerDatabase />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/updates">
        <ProtectedRoute>
          <AppShell>
            <Updates />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/shortlists">
        <ProtectedRoute>
          <AppShell>
            <Shortlists />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/shortlists/:id">
        <ProtectedRoute>
          <AppShell>
            <Shortlists />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/email-management">
        <ProtectedRoute>
          <AppShell>
            <EmailManagement />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/email-settings">
        <ProtectedRoute>
          <EmailSettings />
        </ProtectedRoute>
      </Route>
      
      <Route path="/feedback">
        <ProtectedRoute>
          <AppShell>
            <Feedback />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/profile">
        <ProtectedRoute>
          <AppShell>
            <Profile />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      <Route path="/team">
        <ProtectedRoute>
          <AppShell>
            <TeamManagement />
          </AppShell>
        </ProtectedRoute>
      </Route>
      
      {/* School Profile and Testing pages have been removed */}
      
      {/* Fallback to 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  // Handle OAuth redirect at app level
  useEffect(() => {
    // This will trigger Supabase to check for session in URL
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        console.log("App level: Found session on mount", session.user?.email);
      }
    });
  }, []);

  // Load Calendly scripts globally (but don't initialize badge yet)
  useEffect(() => {
    // Add Calendly CSS if not already present
    if (!document.querySelector('link[href="https://assets.calendly.com/assets/external/widget.css"]')) {
      const calendlyCSS = document.createElement('link');
      calendlyCSS.href = 'https://assets.calendly.com/assets/external/widget.css';
      calendlyCSS.rel = 'stylesheet';
      document.head.appendChild(calendlyCSS);
    }

    // Add Calendly JS if not already present
    if (!document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]')) {
      const calendlyJS = document.createElement('script');
      calendlyJS.src = 'https://assets.calendly.com/assets/external/widget.js';
      calendlyJS.type = 'text/javascript';
      calendlyJS.async = true;
      document.head.appendChild(calendlyJS);
    }
  }, []);
  
  return (
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <OAuthProvider>
          <AuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </OAuthProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
}

export default App;
